/* level-test-app.js — page application for /english-level-test.
 *
 * ESM module: imports the DOM-free engine (cat-engine.js) and owns all UI:
 * mode select → (section intro → [volume check] → items)×N → computing →
 * results (CEFR badge + confidence + per-skill bars + equivalence + can-do +
 * recommendations + share PNG + PDF + history/trend) with pause/resume via
 * localStorage and a quick→full continuation that keeps the posterior.
 *
 * Reads window.__ELT = { T: strings, cfg: { base, locale, links, rtl } }.
 * Test items are ALWAYS rendered LTR English (.elt-en islands) even in RTL UI.
 */

/* Статический импорт, а НЕ `await import(...)`.
 *
 * Верхнеуровневый await требует Chrome >=89 / Safari >=15: движок ниже не
 * РАЗБИРАЕТ модуль, то есть тул для человека не существует вовсе (Sentry
 * TIMBRICA-FRONTEND-E1 — 8 человек). Опустить его конвейер js-min не может:
 * esbuild отказывается собирать TLA под цель es2019 в принципе.
 *
 * Ручная передача ?v= соседу больше не нужна: страница подключает этот вход
 * через moduleGraph, где версия стоит в ПУТИ (/jsv/{ver}/...), и относительный
 * импорт наследует её сам. */
import * as Cat from './cat-engine.js';

const ELT = window.__ELT || { T: {}, cfg: {} };
const T = ELT.T;
const cfg = ELT.cfg;
const $ = (id) => document.getElementById(id);
const root = $('eltPanel');
// The mode select is SERVER-rendered (#eltStartScreen in the blade) so it is on
// screen at first paint; this module wakes only after the whole page has parsed
// and the bank has loaded (7.8 s on 4G + a mid phone on /ikigai-test, the same
// shape, measured 2026-10-08). The node is kept and re-shown by
// renderModeSelect(); a tap that landed on it before init() is handed over by
// window.__earlyAction (layout head).
const startNode = $('eltStartScreen');
const SLUG = 'english-level-test';

const LS_SESSION = 'elt_session_v1';
const LS_HISTORY = 'elt_history_v1';
/* Готовый результат — на случай, что человек уйдёт со страницы и вернётся.
   Это НЕ то же самое, что LS_HISTORY: там сводка (уровень, диапазон, дата),
   которой хватает на строку в таблице и не хватает ни на экран результата, ни
   на документ. Здесь лежит объект finalize() целиком. */
const LS_PENDING = 'elt_pending_v1';
const SESSION_TTL_MS = 24 * 3600 * 1000;
const MAX_PLAYS = 2;

// ── voice set ─────────────────────────────────────────────────────────────
// Three pre-rendered sets per clip, same words, same loudness:
//   uk    MeloTTS, the bank's own accent cast (British-led) — kept by Farid's
//         ear on 2026-09-09, and what every client heard before this chooser;
//   us-f  OpenAI, lead speaker female, dialogue partner male;
//   us-m  OpenAI, lead speaker male, dialogue partner female.
// The accent follows the site-wide __enVariant preference (British by
// default, shared with the grammar course, dictation and the other English
// tools); the American gender is remembered by this tool.
// Real tests (IELTS, Cambridge, EF SET) offer no choice and play a clip once
// or twice, so the chooser sits on the sound-check screen, stays available
// BETWEEN items and is LOCKED while a clip plays: every play spends an
// attempt, whichever voice it was (Farid's rule). `audio` in the bank is the
// uk set — what a client with an older bundle keeps playing — and
// `audioSets` names all three.
const VOICE_SETS = ['uk', 'us-f', 'us-m'];
let usGender = 'f';
try {
    const g = window.__toolSettings && window.__toolSettings.load('english-level-test', 'us_gender', 'f');
    if (g === 'm') usGender = 'm';
} catch (e) {}
let voiceSet = (window.__enVariant && window.__enVariant.isUS()) ? 'us-' + usGender : 'uk';
let voiceLocked = false;       // a clip is playing — the choice waits for `ended`
// ⚠️⚠️ The chooser is offered only when the BANK can honour it. `audioSets`
// arrived on 2026-09-09; a bank from before that names one clip per item, and
// `(u.audioSets && u.audioSets[voiceSet]) || u.audio` then silently plays that
// one clip for all three buttons — a control that answers every press and
// changes nothing. That is what the tool shipped for two days (the bank URL
// carried no generation stamp, so returning visitors ran new code against the
// old bank; fixed at the URL, this is the net under it). A choice we cannot
// honour must not be drawn: the examinee is told nothing false, and the
// mismatch is reported instead of being absorbed.
let voiceSetsUsable = true;
function bankHonoursVoiceSets(listening) {
    return !!(listening && listening.length) && listening.every(
        u => u.audioSets && VOICE_SETS.every(v => typeof u.audioSets[v] === 'string' && u.audioSets[v]));
}
function clipFor(u) { return (u.audioSets && u.audioSets[voiceSet]) || u.audio; }
// ⚠️ The sound-check clip used to be the ONE audio path built inside this file
// (`'audio/v2/calib.' + voiceSet + '.mp3'`), and that is why it kept switching
// voices for the reporter of 2026-09-09 while the items did not: the page knew
// the naming scheme, the bank it was holding did not. The bank names it now,
// with the same content hash every clip carries — the fallback is only for a
// bank old enough not to have the map.
let calibSets = null;
function calibClip() {
    return (calibSets && calibSets[voiceSet]) || ('audio/v2/calib.' + voiceSet + '.mp3');
}
function voiceLabel(v) { return v === 'uk' ? T.voice_british : (v === 'us-m' ? T.voice_american_m : T.voice_american_f); }
function voiceToggleHtml(mini) {
    if (!voiceSetsUsable) return '';
    return `<div class="elt-voice${mini ? ' mini' : ''}" id="eltVoice" role="group" aria-label="${esc(T.voice_label)}">
        <span class="elt-voice-l">${esc(T.voice_label)}</span>
        ${VOICE_SETS.map(v => `<button type="button" data-v="${v}" aria-pressed="${v === voiceSet}">${esc(voiceLabel(v))}</button>`).join('')}
    </div>`;
}
function setVoiceLocked(on) {
    voiceLocked = on;
    const box = $('eltVoice');
    if (!box) return;
    box.classList.toggle('locked', on);
    box.setAttribute('aria-disabled', String(on));
    box.querySelectorAll('button[data-v]').forEach(b => { if (b.dataset.v !== voiceSet) b.disabled = on; });
}
function applyVoice(v, onChange) {
    if (voiceLocked || !VOICE_SETS.includes(v) || v === voiceSet) return;
    voiceSet = v;
    const box = $('eltVoice');
    if (box) box.querySelectorAll('button[data-v]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.v === voiceSet)));
    try {
        if (window.__enVariant) window.__enVariant.set(v === 'uk' ? 'uk' : 'us');
        if (v !== 'uk') { usGender = v.slice(3); window.__toolSettings && window.__toolSettings.save('english-level-test', 'us_gender', usGender); }
    } catch (e) {}
    fe('voice_pick', { v: voiceSet, stage: currentStage });
    announce(voiceLabel(voiceSet));
    if (onChange) onChange(voiceSet);
}
function wireVoiceToggle(onChange) {
    const box = $('eltVoice');
    if (!box) return;
    box.addEventListener('click', (ev) => {
        const b = ev.target.closest('button[data-v]');
        if (b) applyVoice(b.dataset.v, onChange);
    });
}

const LEVELS = Cat.LEVELS;
const LEVEL_NAMES = { A1: T.cefr_a1, A2: T.cefr_a2, B1: T.cefr_b1, B2: T.cefr_b2, C1: T.cefr_c1, C2: T.cefr_c2 };
// CEFR ↔ exam concordance (same data as /english-level-converter)
const ROWS = [
    { cefr: 'A1', ielts: null,       toefl: null,      cambridge: [80, 99],   duo: [10, 25] },
    { cefr: 'A2', ielts: [3.0, 3.5], toefl: null,      cambridge: [100, 119], duo: [30, 55] },
    { cefr: 'B1', ielts: [4.0, 5.0], toefl: [42, 71],  cambridge: [120, 139], duo: [60, 85] },
    { cefr: 'B2', ielts: [5.5, 6.5], toefl: [72, 94],  cambridge: [140, 159], duo: [90, 115] },
    { cefr: 'C1', ielts: [7.0, 8.0], toefl: [95, 113], cambridge: [160, 179], duo: [120, 145] },
    { cefr: 'C2', ielts: [8.5, 9.0], toefl: [114, 120], cambridge: [180, 210], duo: [150, 160] },
];

const SECTION_LABEL = { yesno: T.skill_vocab, grammar: T.skill_grammar, vocab: T.skill_vocab_ctx, reading: T.skill_reading, listening: T.skill_listening };
const SECTION_ORDER = { quick: ['yesno', 'grammar', 'reading'], full: ['yesno', 'grammar', 'vocab', 'reading', 'listening'] };

let bank = null;
let session = null;
let mode = null;
let currentUnit = null;
let currentPassage = null;     // {id, title, passage} shown above testlet questions
let unitShownAt = 0;           // RT anchor
let listeningChecked = false;  // volume check passed this session
let playsUsed = 0;
let testStartMs = 0;
let firedStart = false;
let currentStage = null;       // section currently on screen (for abandon telemetry)
let testCompleted = false;     // finalize() ran — suppresses abandon
let abandonFired = false;      // one abandon event per started test

function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function tr(key, repl) {
    let s = T[key] != null ? String(T[key]) : key;
    if (repl) for (const k in repl) s = s.split(':' + k).join(String(repl[k]));
    return s;
}
function announce(msg) {
    try { window.__announce && window.__announce(String(msg || '')); } catch (e) {}
}
function fe(kind, extra) {
    const p = Object.assign({}, extra || {});
    // Ветка A/B едет в КАЖДОМ событии тула, а не только в событиях оффера:
    // сторожевые метрики эксперимента (доля PDF, доля повторов) считаются
    // именно по этим `f`, и без ветки их не с чем сравнить. 'cal' исключён —
    // это построчный лог рекалибровки, он и так режется по 180 символов.
    if (kind !== 'cal') {
        try {
            const a = window.EltOffer && window.EltOffer.arm();
            if (a) { p.exp = 'elt_offer'; p.arm = a; }
        } catch (e) {}
    }
    try { window.__aeFeature && window.__aeFeature(kind, p); } catch (e) {}
}

// ── audio ─────────────────────────────────────────────────────────────────
//
// Clips are fetched as blobs and played from an object URL: fetch() carries no
// Range header, so the SW's plain CacheFirst route works in every engine
// (Safari refuses 200-for-Range responses from SW caches for media elements).
//
// The trap that shape hides: `play()` must be reached while the click's user
// activation is still alive. Safari drops activation the moment the handler's
// task ends, so the old `fetch(clip).then(play)` was refused with
// NotAllowedError on any link slow enough that the clip outlived the activation
// window — precisely the users a listening test hurts most. Chromium keeps a
// document-sticky activation and played anyway, which is why this never showed
// up locally or in CI. (Bug report 2026-07-08: a Safari user ~8000 km from the
// origin got "нет звука" on every clip; telemetry showed the blobs had arrived
// byte-exact — 218 828 B for l-c2-03 — and only play() failed. Over the
// preceding 5 days 31 of the ~483 sessions that reached the listening section
// lost their audio; Safari's rate, 8/77, was double Chrome's 8/155.)
//
// So the gesture is never crossed:
//   1. prefetchClip() warms the clip when the item renders, so the click
//      handler finds a resolved object URL and calls play() synchronously;
//   2. ONE persistent <audio> element, armed with the shared `__audioGate`
//      primitive (public/js/audio-gate.js), which unlocks it in-gesture with a
//      silent WAV. A clip still in flight can then be played the moment it
//      lands. Measured, not assumed: the unlock is per-element and reusing an
//      element WITHOUT unlocking it does not help — see tests/playwright/audio-gate.mjs.
//
// The remaining share of those 31 sessions died in the fetch, not in play() —
// hence the one retry in prefetchClip() and the honest `cause` in the telemetry.
//
// 2026-07-09: a second report ("Sound does not work", Edge behind a Zscaler
// corporate proxy) died in the fetch too, but with a *response* — `reason:
// 'Error'`, which the code only ever throws for a non-2xx status. Origin logs
// showed 19×200 / 3×304 and no error for any clip that day; the user's clip
// requests never reached the origin at all (mp3 is edge-cached, the sibling
// .json files did reach it, 304). So an intermediary answered for us. That made
// two defects of our own visible, both fixed here:
//   · the status was thrown away (only err.name was logged) — every 403/404/5xx
//     read as 'Error', so the class could not be named. Now `status` ships.
//   · blob-fetch was the ONLY way to get audio, so a filter that blocks binary
//     XHR left the tool mute. playClip() now falls back to streaming the URL
//     straight into the media element — a different request shape. Whether that
//     actually rescues those users is a hypothesis: `feature_use{f:
//     'audio_fallback_direct'}` measures it, and the `status` on the remaining
//     failures will name whatever is left.
//
// Guarded by tests/playwright/english-level-test-audio-gesture.mjs, which
// asserts play() lands inside the activation (--old must fail).

const _clipUrls = new Map();      // path → object URL, ready to play
const _clipFetches = new Map();   // path → in-flight Promise<object URL>
let mediaEl = null;               // the ONE element — never re-created, never nulled
let onClipEnded = null;
let onClipError = null;

// Audio object URLs are page plumbing, not tool output. Without the skip-list
// the global createObjectURL tracker in layouts/app.blade.php fires a phantom
// tool_complete{src:'blob',type:'audio/mpeg'} for the first clip of every page
// load, marking the test "completed" and inflating the funnel.
function blobUrl(blob) {
    try { window.__skipBlobAutoTrack && window.__skipBlobAutoTrack(blob); } catch (e) {}
    return URL.createObjectURL(blob);
}

function clipError(cause, err) {
    const e = new Error('clip_' + cause);
    e.cause = cause;                                   // 'fetch' | 'play'
    e.reason = (err && err.name) || 'Error';           // NotAllowedError, TypeError, …
    // The status is the only thing separating "an intermediary refused the bytes"
    // (403) from "the clip is gone" (404) from "the edge hiccuped" (5xx). Logging
    // err.name alone collapsed all three into 'Error', which is why the 2026-07-09
    // report — 13 clips failed behind a corporate proxy while the origin served
    // 200 to everyone else — could not be root-caused from telemetry. status 0
    // means no response ever arrived; `reason` then carries 'TypeError'.
    e.status = (err && err.status) || 0;
    return e;
}

function clipUrl(path) {
    return cfg.base + '/' + path.replace(/^\//, '');
}

// Set once a blob fetch was refused AND direct streaming played anyway: the
// environment blocks binary fetch of media, so stop paying for the doomed
// request on every later clip (the 07-09 reporter ate 13 × [fetch + retry]).
let directOnly = false;

function prefetchClip(path) {
    if (_clipUrls.has(path)) return Promise.resolve(_clipUrls.get(path));
    if (_clipFetches.has(path)) return _clipFetches.get(path);
    const src = clipUrl(path);
    const attempt = (left) => fetch(src)
        .then(r => {
            if (!r.ok) { const e = new Error('HTTP ' + r.status); e.status = r.status; throw e; }
            return r.blob();
        })
        .catch(err => {
            // A 4xx is a verdict, not a hiccup: retrying a 403/404 only doubles
            // the silence before the fallback gets its turn. Retry the transient
            // shapes — a dropped packet (no status) or a 5xx.
            const transient = !err.status || err.status >= 500;
            if (left <= 0 || !transient) throw err;
            return new Promise(r => setTimeout(r, 500)).then(() => attempt(left - 1));
        });
    const p = attempt(1)
        .then(blob => { const u = blobUrl(blob); _clipUrls.set(path, u); _clipFetches.delete(path); return u; })
        .catch(err => { _clipFetches.delete(path); throw clipError('fetch', err); });
    _clipFetches.set(path, p);
    return p;
}

// Warm a clip unless we already know fetch is blocked here.
function warmClip(path) {
    if (!directOnly) prefetchClip(path).catch(() => {});
}

// The ONE element the whole test plays through. Armed once: __audioGate unlocks
// it on the first eligible gesture and re-arms itself if the permission is ever
// taken back (real iOS does that when a call steals the audio session).
function mediaElement() {
    if (mediaEl) return mediaEl;
    mediaEl = new Audio();
    mediaEl.preload = 'auto';
    mediaEl.playsInline = true;
    mediaEl.setAttribute('playsinline', '');
    mediaEl.addEventListener('ended', () => { const f = onClipEnded; onClipEnded = null; if (f) f(); });
    // A decode/network error AFTER play() resolved never rejects that promise —
    // without this the options stay disabled and the item dead-ends.
    mediaEl.addEventListener('error', () => {
        const f = onClipError; onClipError = null;
        if (f) f(clipError('play', { name: 'MediaError' }));
    });
    if (window.__audioGate) window.__audioGate.arm(mediaEl);
    return mediaEl;
}

// MUST be called synchronously from inside a user-gesture handler.
function unlockMedia() {
    if (window.__audioGate) window.__audioGate.unlockNow(mediaElement());
}

// Plays `path` without ever crossing the user gesture. Call from a click
// handler. Resolves when playback starts; rejects with a clipError().
//
// Two ways to reach the same bytes, and they are refused independently:
//   · fetch() → Blob → object URL — offline-capable (the SW's CacheFirst route
//     sees a Range-less request), but it is an XHR of a binary file, which
//     corporate proxies and endpoint AV routinely block or replace with a block
//     page. That is a real HTTP response, so it surfaces as `status: 403`, not
//     as a network error.
//   · <audio>.src = url — a media-element request (Sec-Fetch-Dest: audio, Range),
//     the shape every ordinary site uses and those filters let through.
// The element is already unlocked by then, so play() is still permitted after the
// failed fetch — that is exactly what __audioGate buys us.
function playClip(path, handlers) {
    const el = mediaElement();
    const h = handlers || {};
    const start = (url) => {
        onClipEnded = h.onEnded || null;
        onClipError = h.onError || null;
        const p = window.__audioGate
            ? window.__audioGate.play(el, url)
            : (el.src !== url ? (el.src = url) : (el.currentTime = 0), el.play());
        return p.catch(err => { throw clipError('play', err); });
    };
    const ready = _clipUrls.get(path);
    if (ready) return start(ready);         // synchronous — activation still alive
    unlockMedia();                          // synchronous — buys the async paths below
    if (directOnly) return start(clipUrl(path));   // also synchronous: the stronger path
    return prefetchClip(path).then(start, (fetchErr) => start(clipUrl(path)).then(
        () => {
            directOnly = true;
            try {
                window.__ae && window.__ae('feature_use', {
                    f: 'audio_fallback_direct', status: fetchErr.status, reason: fetchErr.reason,
                });
            } catch (e) {}
        },
        // Both paths dead. Report the fetch verdict — it carries the status, and
        // a play() error on a URL we could not even download says nothing new.
        () => { fetchErr.fbFailed = 1; throw fetchErr; }
    ));
}

// Pauses playback but KEEPS the element: nulling it would throw away its unlock.
function stopClip() {
    onClipEnded = null;
    onClipError = null;
    if (mediaEl) { try { mediaEl.pause(); } catch (e) {} }
}

// ── rng: seedable via ?seed= for deterministic E2E ────────────────────────

function makeSeed() {
    const q = new URLSearchParams(location.search).get('seed');
    if (q) return (parseInt(q, 10) >>> 0) || 1;
    const a = new Uint32Array(1);
    (window.crypto || {}).getRandomValues ? crypto.getRandomValues(a) : (a[0] = Date.now() >>> 0);
    return a[0] || 1;
}

// ── data loading ──────────────────────────────────────────────────────────

// Bank URLs come from the page WITH a generation stamp (cfg.files) — see the
// comment there. The bare-path form stays only as the fallback for an HTML
// copy old enough not to carry the map: without it such a page would fetch
// nothing at all, which is worse than fetching a possibly stale bank.
function bankUrl(f) {
    return (cfg.files && cfg.files[f]) || (cfg.base + '/' + f + '.json');
}

async function loadBank() {
    const files = ['grammar', 'vocabulary', 'reading', 'listening', 'yesno'];
    const out = await Promise.all(files.map(f =>
        fetch(bankUrl(f)).then(r => { if (!r.ok) throw new Error(f); return r.json(); })));
    return {
        grammar: out[0].items,
        vocab: out[1].items,
        reading: out[2].items,
        listening: out[3].items,
        calib: out[3].calib || null,
        yesno: { real: out[4].real, pseudo: out[4].pseudo },
    };
}

// ── localStorage session ──────────────────────────────────────────────────

function savedSession() {
    try {
        const raw = localStorage.getItem(LS_SESSION);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || !data.state || (Date.now() - (data.savedAt || 0)) > SESSION_TTL_MS) return null;
        return data;
    } catch (e) { return null; }
}
function saveSession() {
    if (!session) return;
    try {
        localStorage.setItem(LS_SESSION, JSON.stringify({
            state: session.serialize(), savedAt: Date.now(),
            listeningChecked, testStartMs,
        }));
    } catch (e) {}
}
function clearSession() { try { localStorage.removeItem(LS_SESSION); } catch (e) {} }

/* ── готовый результат переживает уход со страницы ─────────────────────────
 *
 * ⚠️⚠️ До 10.09.2026 экран результата существовал ТОЛЬКО как объект в памяти
 * вкладки: renderResults() звался ниоткуда, кроме finishTest(), а init() на
 * любой загрузке шёл в renderModeSelect(). Значит любой уход — регистрация,
 * /login, fail-open токен-стены, просто перезагрузка — уничтожал двадцать
 * минут работы человека безвозвратно, и вернуться к покупке сертификата было
 * НЕОТКУДА: кнопку «Получить — 99 ₽» рисует только тот самый экран.
 *
 * Замер за 30 дней до починки: 2033 сессии видели блок документов, 585
 * отложили прогон на сервер, 12 нажали «оплатить», 5 купили, и лишь 2
 * отложенных прогона были когда-либо забраны. Жалоба 09.09.2026 («прошла
 * тест, хотела купить сертификат за 99 р, а все сбросилось») разобрана по
 * телеметрии до секунды: park_ok прошёл, через четыре минуты человек вернулся
 * на /ru/english-level-test и получил экран выбора режима.
 *
 * ⚠️ В комментарии renderAnon() (level-test-offer.js) стояло обратное —
 * «результат при этом не теряется, он лежит в localStorage (LS_HISTORY)».
 * Это было неправдой: LS_HISTORY хранит сводку, и ни одна строка кода её для
 * восстановления не читала. Утверждение четыре месяца отвечало «всё в
 * порядке» на вопрос, который стоило проверить.
 *
 * Приём взят у /iq-test, где та же болезнь вылечена 26.08 (iq_report_pending_
 * result + restorePendingResult): второй способ решать одну задачу разъехался
 * бы с первым на ближайшей правке.
 *
 * Сутки — намеренно: возврат «через неделю» к чужому уже состоянию сбивает с
 * толку сильнее, чем чистый стартовый экран.
 */
function savePending(result, m) {
    try {
        localStorage.setItem(LS_PENDING, JSON.stringify({ at: Date.now(), mode: m, result }));
    } catch (e) { /* приватный режим — не переживёт, но и не уронит экран */ }
}
function pendingResult() {
    try {
        const raw = localStorage.getItem(LS_PENDING);
        if (!raw) return null;
        const o = JSON.parse(raw);
        if (!o || !o.result || !o.result.level) { clearPending(); return null; }
        if ((Date.now() - (o.at || 0)) > SESSION_TTL_MS) { clearPending(); return null; }
        return o;
    } catch (e) { return null; }
}
function clearPending() { try { localStorage.removeItem(LS_PENDING); } catch (e) {} }

function history() {
    try { return JSON.parse(localStorage.getItem(LS_HISTORY) || '[]'); } catch (e) { return []; }
}
function pushHistory(entry) {
    try {
        const h = history();
        h.push(entry);
        localStorage.setItem(LS_HISTORY, JSON.stringify(h.slice(-30)));
    } catch (e) {}
}

// ── panels ────────────────────────────────────────────────────────────────

function setPanel(html) {
    // `html` may be a function that builds the panel itself — the mode select
    // re-attaches the server-rendered node instead of writing markup.
    if (typeof html === 'function') html(); else root.innerHTML = html;
    const top = root.getBoundingClientRect().top;
    if (top < 0) root.scrollIntoView({ block: 'nearest' });
}

function setFocusMode(on) {
    // hide footer blocks while testing (iq-test precedent) — fewer exits mid-test
    const f = document.querySelector('.tool-page-footer');
    if (f) f.style.display = on ? 'none' : '';
}

// ── mode select ───────────────────────────────────────────────────────────

// The blade owns the mode cards' markup (one source of truth); this function
// puts the dynamic parts around them — the «continue» card above, the local
// history and the account's past runs below. A page served from a cache older
// than the blade has no #eltStartScreen — say so honestly instead of keeping a
// second copy of the markup here. Returns whether an unfinished run is offered
// (init drops an early tap then: the person had not seen that run when they tapped).
let startWired = false;
function renderModeSelect() {
    setFocusMode(false);
    root.removeAttribute('data-boot');
    if (!startNode) {
        setPanel('<div class="elt-card lg-card elt-center">' + esc(T.load_error) + '</div>');
        return false;
    }
    if (!startWired) {
        startWired = true;
        startNode.querySelector('#eltStartFull').addEventListener('click', () => startTest('full'));
        startNode.querySelector('#eltStartQuick').addEventListener('click', () => startTest('quick'));
    }
    const saved = savedSession();
    const hist = history();
    let resumeHtml = '';
    if (saved) {
        const sec = SECTION_LABEL[restoredSectionName(saved)] || '';
        resumeHtml = `
        <div class="elt-resume lg-card" id="eltResume">
            <div class="elt-resume-t">${esc(T.resume_title)}</div>
            <div class="elt-resume-d">${esc(tr('resume_desc', { section: sec }))}</div>
            <div class="elt-row">
                <button type="button" class="elt-btn" id="eltResumeBtn">${esc(T.btn_resume)}</button>
                <button type="button" class="elt-btn ghost" id="eltStartOverBtn">${esc(T.btn_start_over)}</button>
            </div>
        </div>`;
    }
    const histHtml = hist.length ? `
        <details class="elt-hist-toggle"><summary>${esc(tr('history_summary', { n: hist.length }))}</summary>
        <div id="eltHistList">${historyListHtml(hist)}</div></details>` : '';

    setPanel(() => {
        root.textContent = '';
        if (resumeHtml) root.insertAdjacentHTML('beforeend', resumeHtml);
        root.appendChild(startNode);
        root.insertAdjacentHTML('beforeend', `${histHtml}
    <div id="eltPastSlot"></div>`);
    });

    // Плашка «вы проходите тест для класса N» рисуется своим модулем и ДО
    // теста: узнать, что класс закрыт или переполнен, человек обязан до
    // двадцати минут работы, а не после них.

    // Прежние прохождения из АККАУНТА — вход в платные документы для того, кто
    // пришёл не сегодня. Блок истории выше живёт в localStorage и купить по
    // нему нельзя: у браузерных записей нет id прогона.
    if (window.EltOffer && window.EltOffer.mountPast) window.EltOffer.mountPast();
    if (saved) {
        $('eltResumeBtn').addEventListener('click', () => resumeTest(saved));
        $('eltStartOverBtn').addEventListener('click', () => { clearSession(); fe('resume_discarded'); renderModeSelect(); });
    }
    const histList = $('eltHistList');
    if (histList) bindHistoryClear();
    return !!saved;
}

/** Take the mode buttons over from window.__earlyAction. `replay` — whether a
    tap made before init() still means what it meant: only when the mode select
    is what init shows and no unfinished run sits above it (a restored result or
    resume drop the tap — startTest() would wipe a run the person never saw). */
function claimEarly(replay) {
    if (!window.__earlyAction) return;
    window.__earlyAction.claim(SLUG, replay ? (m) => startTest(m === 'quick' ? 'quick' : 'full') : null);
}

function restoredSectionName(saved) {
    // best-effort label for the resume banner from served counts
    const st = saved.state || {};
    const sk = st.servedBySkill || {};
    if ((st.mode === 'full') && sk.listening > 0) return 'listening';
    if ((st.testletsServed || 0) > 0) return 'reading';
    if (sk.vocab > 0) return 'vocab';
    if (sk.grammar > 0) return 'grammar';
    return 'yesno';
}

// ── test flow ─────────────────────────────────────────────────────────────

function startTest(m) {
    mode = m;
    testCompleted = false; abandonFired = false;
    // Новый прогон отменяет отложенный: иначе уход с середины второго теста
    // вернул бы человека к результату ПЕРВОГО, и это читалось бы как «тест
    // сбросился обратно».
    clearPending();
    fe('mode_chosen', { mode: m });
    if (!firedStart) { firedStart = true; try { window.__ae && window.__ae('tool_use', { tool_slug: 'english-level-test' }); } catch (e) {} }
    testStartMs = Date.now();
    listeningChecked = false;
    session = Cat.createSession(bank, { mode: m, rng: Cat.makeRng(makeSeed()), nowMs: testStartMs });
    setFocusMode(true);
    advance(true);
}

function resumeTest(saved) {
    try {
        testCompleted = false; abandonFired = false;
        session = Cat.restoreSession(bank, saved.state, { rng: Cat.makeRng(makeSeed()) });
        mode = saved.state.mode;
        listeningChecked = !!saved.listeningChecked;
        testStartMs = saved.testStartMs || Date.now();
        fe('resumed', { mode });
        setFocusMode(true);
        advance(true);
    } catch (e) {
        clearSession();
        renderModeSelect();
    }
}

function advance(forceIntro) {
    // save BEFORE serving: if the user closes the tab mid-item, resume
    // re-serves a fresh item instead of silently losing the in-flight one
    saveSession();
    const res = session.next();
    if (res.done) { finishTest(); return; }
    currentUnit = res.unit;
    if (res.unit.kind === 'passage') {
        currentPassage = { id: res.unit.id, title: res.unit.title, passage: res.unit.passage };
    }
    if ((res.sectionChanged || forceIntro) && res.section !== 'yesno') {
        renderSectionIntro(res.section, res);
        return;
    }
    if ((res.sectionChanged || forceIntro) && res.section === 'yesno') {
        renderSectionIntro('yesno', res);
        return;
    }
    renderUnit(res);
}

function sectionDots(active) {
    const order = SECTION_ORDER[mode] || [];
    return '<div class="elt-dots">' + order.map(s =>
        `<span class="elt-dot${s === active ? ' on' : ''}">${esc(SECTION_LABEL[s] || s)}</span>`).join('') + '</div>';
}

function progressBar() {
    const p = Math.round(session.progress() * 100);
    return `<div class="elt-prog"><div class="elt-prog-f" style="width:${p}%"></div></div>
            <div class="elt-prog-lbl">${esc(p >= 100 ? T.progress_finished_early : T.progress_stabilizing)}</div>`;
}

function renderSectionIntro(section, res) {
    currentStage = section;
    fe('stage_enter', { stage: section, mode });
    const order = SECTION_ORDER[mode] || [];
    const idx = order.indexOf(section) + 1;
    const needVolume = section === 'listening' && !listeningChecked;
    setPanel(`
    ${sectionDots(section)}
    <div class="elt-card lg-card elt-center">
        <div class="elt-sec-n">${esc(tr('section_intro_title', { n: idx, total: order.length }))}</div>
        <div class="elt-sec-name">${esc(SECTION_LABEL[section] || section)}</div>
        <div class="elt-sec-d">${esc(T['section_desc_' + section] || '')}</div>
        ${needVolume ? `
        <div class="elt-volcheck">
            <div class="elt-volcheck-t">${esc(T.volume_check_title)}</div>
            <div class="elt-sec-d">${esc(T.volume_check_desc)}</div>
            ${voiceToggleHtml(false)}
            ${voiceSetsUsable ? `<div class="elt-voice-hint">${esc(T.voice_hint)}</div>` : ''}
            <div class="elt-row elt-center-row">
                <button type="button" class="elt-btn ghost" id="eltVolPlay">${esc(T.btn_play_sample)}</button>
                <button type="button" class="elt-btn" id="eltVolOk" disabled>${esc(T.btn_can_hear)}</button>
            </div>
        </div>` : `
        <button type="button" class="elt-btn" id="eltSecGo">${esc(T.btn_continue)}</button>`}
    </div>`);
    if (needVolume) {
        const playBtn = $('eltVolPlay'), okBtn = $('eltVolOk');
        // Warm the sample while the user reads the section intro, so the click
        // below plays it synchronously and unlocks the element for the clips.
        warmClip(calibClip());
        // switching the voice stops the sample and warms the other one; the
        // «I can hear it» button keeps whatever state the first play earned
        wireVoiceToggle(() => { stopClip(); warmClip(calibClip()); });
        playBtn.addEventListener('click', () => {
            playClip(calibClip())
                .then(() => { okBtn.disabled = false; })
                .catch((err) => {
                    // never dead-end the test on a broken calib clip — let the
                    // user proceed; playClip already tried direct streaming
                    okBtn.disabled = false;
                    window.__toast && window.__toast(T.audio_error, 'error');
                    try { window.__aeToolError && window.__aeToolError({ error_type: 'calib_audio_failed', cause: err.cause, reason: err.reason, status: err.status, fb: err.fbFailed || 0 }); } catch (e) {}
                });
        });
        okBtn.addEventListener('click', () => {
            unlockMedia();          // a real gesture — insurance if the sample never played
            stopClip();
            listeningChecked = true;
            fe('volume_check', { ok: 1 });
            saveSession();
            renderUnit({ section, unit: currentUnit });
        });
    } else {
        $('eltSecGo').addEventListener('click', () => {
            // Resume path: the volume check is already ticked off, so this is the
            // last gesture before the clips. Spend it on the unlock.
            if (section === 'listening') unlockMedia();
            renderUnit({ section, unit: currentUnit });
        });
    }
}

function renderUnit(res) {
    const u = currentUnit;
    currentStage = res.section;
    if (!u) { advance(); return; }
    if (u.kind === 'passage') { renderPassage(res.section); return; }
    if (u.kind === 'yesno') { renderYesno(res.section); return; }
    renderMcq(res.section);
}

function renderPassage(section) {
    setPanel(`
    ${sectionDots(section)}
    ${progressBar()}
    <div class="elt-card lg-card">
        <div class="elt-passage elt-en" lang="en" dir="ltr">
            <div class="elt-passage-t">${esc(currentPassage.title)}</div>
            <div class="elt-passage-b">${esc(currentPassage.passage)}</div>
        </div>
        <div class="elt-sec-d">${esc(T.passage_read_then)}</div>
        <button type="button" class="elt-btn" id="eltPassGo">${esc(T.btn_to_questions)}</button>
    </div>`);
    $('eltPassGo').addEventListener('click', () => advance());
}

function renderYesno(section) {
    const u = currentUnit;
    unitShownAt = performance.now();
    setPanel(`
    ${sectionDots(section)}
    ${progressBar()}
    <div class="elt-card lg-card elt-center">
        <div class="elt-yn-hint">${esc(T.yesno_instruction)}</div>
        <div class="elt-yn-word elt-en" lang="en" dir="ltr">${esc(u.word)}</div>
        <div class="elt-row elt-center-row" data-answer role="group" aria-label="${esc(T.yesno_instruction)}">
            <button type="button" class="elt-btn ghost elt-yn-btn" id="eltNo"><span class="elt-kbd" aria-hidden="true">1</span>${esc(T.yesno_dont_know)}</button>
            <button type="button" class="elt-btn elt-yn-btn" id="eltYes"><span class="elt-kbd" aria-hidden="true">2</span>${esc(T.yesno_know)}</button>
        </div>
    </div>`);
    announce(T.yesno_instruction + ': ' + u.word);
    const answer = (val) => {
        session.record(u, val, { rt_ms: Math.round(performance.now() - unitShownAt) });
        saveSession();
        advance();
    };
    $('eltYes').addEventListener('click', () => answer(true));
    $('eltNo').addEventListener('click', () => answer(false));
}

function renderMcq(section) {
    const u = currentUnit;
    const isListening = !!u.audio;
    playsUsed = 0;
    let audioFailed = false;
    unitShownAt = performance.now();
    const passageHtml = (u.testletId && currentPassage && currentPassage.id === u.testletId)
        ? `<div class="elt-passage elt-passage-mini elt-en" lang="en" dir="ltr">
             <div class="elt-passage-t">${esc(currentPassage.title)}</div>
             <div class="elt-passage-b">${esc(currentPassage.passage)}</div></div>`
        : '';
    const audioHtml = isListening ? `
        <div class="elt-audio">
            <button type="button" class="elt-play" id="eltPlay" aria-label="${esc(T.btn_play)}">
                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
            </button>
            <div class="elt-plays" id="eltPlays">${esc(tr('plays_left', { n: MAX_PLAYS }))}</div>
            ${voiceToggleHtml(true)}
        </div>
        ${voiceSetsUsable ? `<div class="elt-voice-next" id="eltVoiceNext" hidden>${esc(T.voice_applies_next)}</div>` : ''}
        <div class="elt-listen-hint" id="eltListenHint">${esc(T.listen_first)}</div>` : '';
    setPanel(`
    ${sectionDots(section)}
    ${progressBar()}
    <div class="elt-card lg-card">
        ${passageHtml}
        ${audioHtml}
        <div class="elt-stem elt-en" lang="en" dir="ltr">${esc(u.stem)}</div>
        <div class="elt-opts" id="eltOpts" data-answer role="group" aria-label="${esc(u.stem)}">
            ${u.options.map((o, i) => `<button type="button" class="elt-opt elt-en" lang="en" dir="ltr" data-pos="${i}" aria-label="${esc(o)}" ${isListening ? 'disabled' : ''}><span class="elt-kbd" aria-hidden="true">${i + 1}</span><span>${esc(o)}</span></button>`).join('')}
        </div>
    </div>`);
    announce(isListening ? T.listen_first : u.stem);

    if (isListening) {
        const playBtn = $('eltPlay'), playsEl = $('eltPlays');
        // Warm the clip while the user is still reading the stem, so the click
        // handler below reaches play() synchronously (see the audio section).
        warmClip(clipFor(u));
        // Between plays the chooser is live and warms the other set; WHILE a
        // clip plays it is locked (setVoiceLocked) — the options unlock on
        // `ended`, and a stopped clip never fires it.
        wireVoiceToggle(() => warmClip(clipFor(u)));
        playBtn.addEventListener('click', () => {
            if (playsUsed >= MAX_PLAYS) return;
            playsUsed++;
            playsEl.textContent = tr('plays_left', { n: MAX_PLAYS - playsUsed });
            // ⚠️ With the last attempt spent the chooser can no longer change
            // what THIS item sounds like — only the next one. Left unsaid, it
            // is the same «pressed it and nothing happened» the tool was
            // reported for on 2026-09-09, one floor down: 106 sessions in four
            // days switch voices between items, and this is where they land.
            if (playsUsed >= MAX_PLAYS) {
                playBtn.classList.add('off');
                const nx = $('eltVoiceNext');
                if (nx) nx.hidden = false;
            }
            setVoiceLocked(true);
            let handled = false;
            const enableOpts = () => {
                setVoiceLocked(false);
                document.querySelectorAll('.elt-opt').forEach(b => b.disabled = false);
                const hint = $('eltListenHint');
                if (hint) hint.remove();
            };
            const failed = (err) => {
                if (handled) return;                     // play() rejection and the
                handled = true;                          // 'error' event can both fire
                setVoiceLocked(false);
                playsUsed = Math.max(0, playsUsed - 1);  // heard nothing → no credit spent
                audioFailed = true;
                playsEl.textContent = tr('plays_left', { n: MAX_PLAYS - playsUsed });
                // …and the button must lose `off` too, or it stays dimmed but
                // clickable — that mismatch was the rage-click signature in the
                // 07-08 report (3 clicks on a `.elt-play.off` that still worked).
                playBtn.classList.remove('off');
                const nx = $('eltVoiceNext');
                if (nx) nx.hidden = true;
                window.__toast && window.__toast(T.audio_error, 'error');
                try { window.__aeToolError && window.__aeToolError({ error_type: 'listen_audio_failed', id: u.id, cause: err && err.cause, reason: err && err.reason, status: (err && err.status) || 0, fb: (err && err.fbFailed) || 0 }); } catch (e) {}
                // don't dead-end the test on a broken clip: let them move on —
                // the answer is recorded as VOID (never scored: a clip the user
                // could not hear must not update the ability estimate)
                enableOpts();
            };
            playClip(clipFor(u), { onEnded: enableOpts, onError: failed })
                .then(() => { if (playsUsed === 1) unitShownAt = performance.now(); })  // RT starts at first play
                .catch(failed);
        });
    }

    $('eltOpts').addEventListener('click', (ev) => {
        const btn = ev.target.closest('.elt-opt');
        if (!btn || btn.disabled) return;
        document.querySelectorAll('.elt-opt').forEach(b => b.disabled = true);
        btn.classList.add('picked');
        session.record(u, parseInt(btn.dataset.pos, 10), { rt_ms: Math.round(performance.now() - unitShownAt), void: audioFailed });
        saveSession();
        stopClip();
        setTimeout(() => advance(), 220);
    });
}

// ── results ───────────────────────────────────────────────────────────────

function finishTest() {
    setPanel(`<div class="elt-card lg-card elt-center"><div class="elt-spinner"></div><div class="elt-sec-d">${esc(T.computing)}</div></div>`);
    setTimeout(() => {
        const result = session.finalize({ nowMs: Date.now() });
        testCompleted = true;
        clearSession();
        fe('test_complete', {
            mode, items: result.nScored, sec: result.durMs ? Math.round(result.durMs / 1000) : null,
            level: result.level, conf: +(result.levelP).toFixed(2), withheld: result.levelWithheld ? 1 : 0,
        });
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                mode, engine_v: 1, bank_v: 1,
                theta: +result.theta.toFixed(2), se: +result.seReported.toFixed(2),
                level: result.level, level_p: +result.levelP.toFixed(2),
                range: result.rangeLo + '-' + result.rangeHi,
                th_yn: result.perSkill.yesno ? +result.perSkill.yesno.theta.toFixed(2) : null,
                th_gr: result.perSkill.grammar ? +result.perSkill.grammar.theta.toFixed(2) : null,
                th_vc: result.perSkill.vocab ? +result.perSkill.vocab.theta.toFixed(2) : null,
                th_rd: result.perSkill.reading ? +result.perSkill.reading.theta.toFixed(2) : null,
                th_ls: result.perSkill.listening ? +result.perSkill.listening.theta.toFixed(2) : null,
                n: result.nScored, dur_s: result.durMs ? Math.round(result.durMs / 1000) : null,
                validity_ok: result.validity.flags.length ? 0 : 1,
                validity_reasons: result.validity.flags.join(','),
            });
            // Per-item recalibration log goes as separate small events: the
            // analytics endpoint truncates string props to 200 chars once a
            // payload exceeds 1 KB, which would gut the full-test item log.
            const itemsStr = session.telemetryItems();
            const CHUNK = 180;
            const total = Math.ceil(itemsStr.length / CHUNK);
            for (let i = 0; i < total; i++) {
                fe('cal', { seq: i + 1, total, d: itemsStr.slice(i * CHUNK, (i + 1) * CHUNK) });
            }
        } catch (e) {}
        pushHistory({
            ts: Date.now(), mode, level: result.level, levelP: +result.levelP.toFixed(2),
            lo: result.rangeLo, hi: result.rangeHi, theta: +result.theta.toFixed(2),
            se: +result.seReported.toFixed(2), withheld: result.levelWithheld ? 1 : 0,
        });
        // Кладём ДО отрисовки: между экраном и первым нажатием человек уже
        // может уйти (вкладку закрыли, ушли по ссылке оффера), и заначка,
        // сделанная «при уходе», этих случаев не покрывает.
        savePending(result, mode);
        renderResults(result);
    }, 900);
}

function thetaToPct(th) {
    const lo = -3.0, hi = 2.8;
    return Math.max(2, Math.min(98, (th - lo) / (hi - lo) * 100));
}

function skillBar(label, sub, locked) {
    if (locked || !sub) {
        return `<div class="elt-skill dim">
            <div class="elt-skill-l">${esc(label)}</div>
            <div class="elt-skill-tr"><div class="elt-skill-lock">${esc(T.skill_locked)}</div></div>
            <div class="elt-skill-lv">—</div></div>`;
    }
    const pos = thetaToPct(sub.theta);
    const w1 = thetaToPct(sub.theta - sub.se), w2 = thetaToPct(sub.theta + sub.se);
    return `<div class="elt-skill">
        <div class="elt-skill-l">${esc(label)}</div>
        <div class="elt-skill-tr">
            <div class="elt-skill-ci" style="left:${w1}%;width:${Math.max(2, w2 - w1)}%"></div>
            <div class="elt-skill-pt" style="left:${pos}%"></div>
            ${LEVELS.map((l, i) => `<span class="elt-skill-tick" style="left:${(i + 0.5) / 6 * 100}%">${l}</span>`).join('')}
        </div>
        <div class="elt-skill-lv">${esc(sub.level)}</div></div>`;
}

function confidenceStars(result) {
    let stars = 5;
    if (mode === 'quick') stars--;
    if (result.validity.flags.includes('too_fast')) stars--;
    if (result.validity.flags.length && !result.validity.flags.includes('too_fast')) stars--;
    if (result.seReported > 0.45) stars--;
    stars = Math.max(1, stars);
    return '★'.repeat(stars) + '☆'.repeat(5 - stars);
}

function renderResults(result) {
    setFocusMode(false);
    // The whole point of measuring a level is that nothing downstream has to ask
    // for it again: remember it once, and every sibling language tool reads it
    // through window.__cefr. When the test WITHHELD the level (confidence too
    // low to state one), nothing is remembered and no link carries a level —
    // handing on a number we decline to show would be worse than asking.
    try {
        if (window.__cefr && result.level && !result.levelWithheld) {
            window.__cefr.set(result.level, 'level-test');
        }
    } catch (e) {}
    const flags = result.validity.flags;
    const flagHtml = flags.length ? `
        <div class="elt-flags">
            <div class="elt-flags-t">${esc(T.validity_title)}</div>
            ${flags.map(f => `<div class="elt-flag">${esc(T['flag_' + f] || f)}</div>`).join('')}
        </div>` : '';

    const headHtml = result.levelWithheld ? `
        <div class="elt-withheld">
            <div class="elt-withheld-t">${esc(T.withheld_title)}</div>
            <div class="elt-range-line">${esc(tr('result_range', { lo: result.rangeLo, hi: result.rangeHi }))}</div>
            <div class="elt-sec-d">${esc(T.withheld_desc)}</div>
        </div>` : `
        <div class="elt-badge-wrap">
            <div class="elt-badge elt-badge-${result.level.toLowerCase()}">${esc(result.level)}</div>
            <div class="elt-badge-name">${esc(LEVEL_NAMES[result.level] || '')}</div>
            <div class="elt-range-line">${esc(tr('result_likely', { p: Math.round(result.levelP * 100) }))} · ${esc(tr('result_range', { lo: result.rangeLo, hi: result.rangeHi }))}</div>
            <div class="elt-conf" title="${esc(T.confidence_label)}">${confidenceStars(result)} <span class="elt-conf-l">${esc(T.confidence_label)}</span></div>
        </div>`;

    const ps = result.perSkill;
    const vocabSub = ps.yesno || ps.vocab;
    const skillsHtml = `
        <div class="elt-skills">
            <div class="elt-block-t">${esc(T.per_skill_title)}</div>
            ${skillBar(T.skill_vocab, vocabSub, false)}
            ${skillBar(T.skill_grammar, ps.grammar, false)}
            ${skillBar(T.skill_reading, ps.reading, false)}
            ${skillBar(T.skill_listening, ps.listening, mode === 'quick')}
        </div>`;

    const upsellHtml = mode === 'quick' ? `
        <button type="button" class="elt-btn" id="eltUpgrade">${esc(T.btn_full_upsell)}</button>` : '';

    const userRow = ROWS.findIndex(r => r.cefr === result.level);
    const fmtR = (r, dec) => r ? (dec ? r[0].toFixed(1) + '–' + r[1].toFixed(1) : r[0] + '–' + r[1]) : '—';
    const equivHtml = `
        <div class="elt-equiv">
            <div class="elt-block-t">${esc(T.equiv_title)}</div>
            <table class="elt-table">
                <thead><tr><th>CEFR</th><th>IELTS</th><th>TOEFL iBT</th><th>Cambridge</th><th>Duolingo</th></tr></thead>
                <tbody>${ROWS.map((r, i) => `
                    <tr class="${i === userRow ? 'hit' : Math.abs(i - userRow) === 1 ? 'near' : ''}">
                        <td>${r.cefr}</td><td>${fmtR(r.ielts, true)}</td><td>${fmtR(r.toefl, false)}</td>
                        <td>${fmtR(r.cambridge, false)}</td><td>${fmtR(r.duo, false)}</td></tr>`).join('')}
                </tbody>
            </table>
            ${cfg.links.converter ? `<a class="elt-link" href="${esc(cfg.links.converter.href)}">${esc(T.equiv_note)}</a>` : ''}
        </div>`;

    const canDo = T['cando_' + result.level.toLowerCase()] || [];
    const canDoHtml = canDo.length ? `
        <div class="elt-cando">
            <div class="elt-block-t">${esc(T.can_do_title)}</div>
            <ul>${canDo.map(c => `<li>${esc(c)}</li>`).join('')}</ul>
        </div>` : '';

    const recsHtml = buildRecs(result);

    const vocabHtml = `
        <div class="elt-vsize">
            <div class="elt-block-t">${esc(T.vocab_size_label)}</div>
            <div class="elt-vsize-n">≈ ${result.vocabSize.toLocaleString()}</div>
            ${cfg.links.vocab ? `<a class="elt-link" href="${esc(cfg.links.vocab.href)}">${esc(T.vocab_size_link)}</a>` : ''}
        </div>`;

    setPanel(`
    <div id="eltResults">
        <div class="elt-card lg-card elt-center">
            ${headHtml}
            ${flagHtml}
        </div>
        <div id="eltClassResultSlot"></div>
        <div class="elt-card lg-card">${skillsHtml}${upsellHtml}</div>
        <div id="eltOfferSlot"></div>
        <div class="elt-card lg-card elt-two">${vocabHtml}${canDoHtml}</div>
        <div class="elt-card lg-card">${equivHtml}</div>
        ${recsHtml}
        <details class="elt-method"><summary>${esc(T.methodology_title)}</summary><p>${esc(T.methodology_body)}</p></details>
        <div class="elt-actions">
            <button type="button" class="elt-btn" id="eltShare">${esc(T.btn_share_png)}</button>
            <button type="button" class="elt-btn ghost" id="eltPdf">${esc(T.btn_pdf)}</button>
            <button type="button" class="elt-btn ghost" id="eltRetake">${esc(T.btn_retake)}</button>
        </div>
        ${historyBlock()}
    </div>`);

    $('eltShare').addEventListener('click', () => { fe('share_png'); shareCard(result); });
    $('eltPdf').addEventListener('click', () => { fe('pdf'); exportPdf(result); });
    // «Пройти заново» — намеренный отказ от этого результата. Не снять заначку
    // здесь значило бы вернуть человека к нему же, если он уйдёт со страницы
    // выбора режима, не начав второй тест.
    $('eltRetake').addEventListener('click', () => { fe('retest'); clearPending(); renderModeSelect(); });
    const up = $('eltUpgrade');
    if (up) up.addEventListener('click', () => {
        fe('upgrade_to_standard');
        // ⚠️ Экран мог быть ВОССТАНОВЛЕН из заначки — тогда живого прогона нет
        // (finalize() отработал в прошлой загрузке страницы), и upgradeToFull()
        // на null уронил бы кнопку с TypeError. Дописать к чужому прогону
        // нечего, поэтому честный ответ — начать полный тест.
        if (!session) { startTest('full'); return; }
        session = session.upgradeToFull();
        mode = 'full';
        testStartMs = testStartMs || Date.now();
        setFocusMode(true);
        advance(true);
    });
    bindHistoryClear();
    drawTrend();

    // Дверь аккаунта. Модуль сам решает, рисовать ли себя (ветка A/B) и в каком
    // состоянии (гость / уже вошёл). Отсутствие модуля не должно ломать экран
    // результата — он и без него полностью рабочий.
    try {
        if (window.EltOffer) {
            result.__mode = mode;
            result.__handoff = buildHandoff(result);
            window.EltOffer.mount(result);
        }
    } catch (e) {}

    // Сдача в класс. Модуль сам решает, есть ли класс в адресе; его отсутствие
    // не должно ломать экран результата.
    try {
        if (window.EltClass) window.EltClass.mount(result);
    } catch (e) {}
}

/* ── что тест УЖЕ знает про этого человека и может отдать соседним тулам ──
 *
 * Экран результата двадцать минут собирал самый точный материал для учёбы,
 * какой на сайте вообще бывает: конкретные СЛОВА, которые этот человек не
 * опознал, и конкретные КОНСТРУКЦИИ, в которых он ошибся. До 09.2026 всё это
 * умирало вместе с вкладкой, а рядом стоял /vocabulary-trainer с 45 сессиями
 * в месяц при 37 тыс. на самом тесте. Замер и был поводом: главный вход
 * рубрики не передавал наружу ничего, кроме уровня.
 *
 * ⚠️ Считается ЗДЕСЬ, а не в offer-модуле: банк заданий загружен в этом
 * файле, и просить его второй раз означало бы второй источник правды для
 * текста слова. Offer получает готовый список.
 *
 * ⚠️ Только неверные ответы. У yes/no это «настоящее слово, про которое
 * человек сказал "не знаю"» — псевдослова в список попасть не могут в
 * принципе: их в банке нет среди `real`, а сказать «не знаю» про псевдослово
 * — это ПРАВИЛЬНЫЙ ответ, и учить там нечего.
 */
function buildHandoff(result) {
    const out = { words: [], topics: [] };
    if (!bank || !Array.isArray(result.answers) || !result.answers.length) return out;

    const byId = new Map();
    (bank.yesno && bank.yesno.real || []).forEach(w => byId.set(w.id, { w: w.word, lv: w.level }));
    (bank.vocab || []).forEach(it => { if (it.target) byId.set(it.id, { w: it.target, lv: it.level }); });
    const grammarById = new Map();
    (bank.grammar || []).forEach(it => { if (it.construct) grammarById.set(it.id, it); });

    const seenWord = new Set();
    const topicCount = new Map();

    for (const a of result.answers) {
        if (!a || a.o) continue;                       // верный ответ — учить нечего
        const word = byId.get(a.i);
        if (word && word.w && !seenWord.has(word.w)) {
            seenWord.add(word.w);
            out.words.push(word);
            continue;
        }
        const gr = grammarById.get(a.i);
        if (gr) {
            const prev = topicCount.get(gr.construct);
            topicCount.set(gr.construct, { id: gr.construct, lv: gr.level, n: (prev ? prev.n : 0) + 1 });
        }
    }

    // Тем показываем немного и самые частые: список из пятнадцати конструкций
    // это не разбор, а второй способ сказать «у вас B1».
    out.topics = Array.from(topicCount.values()).sort((a, b) => b.n - a.n).slice(0, 4);

    return out;
}

/* A recommendation link used to drop the number the test had just produced, so
   the drill it opened served A1…C1 material at random — measured on RU prod:
   1086 sessions walked level-test → /phrasal-verbs in 90 days, into a trainer
   with no level at all. ?level= is only appended when a level was actually
   stated (window.__cefr holds nothing after a withheld result). */
function recLink(href) {
    try { return (window.__cefr && href) ? window.__cefr.link(href) : href; }
    catch (e) { return href; }
}

function buildRecs(result) {
    const ps = result.perSkill;
    const cand = [
        ['vocab', ps.yesno ? ps.yesno.theta : Infinity, [cfg.links.wordday, cfg.links.vocab]],
        ['grammar', ps.grammar ? ps.grammar.theta : Infinity, [cfg.links.irregular, cfg.links.phrasal]],
        ['reading', ps.reading ? ps.reading.theta : Infinity, [cfg.links.collocations]],
        ['listening', ps.listening ? ps.listening.theta : Infinity, [cfg.links.dictation]],
    ].filter(c => c[1] !== Infinity);
    if (!cand.length) return '';
    cand.sort((a, b) => a[1] - b[1]);
    const weakest = cand[0];
    const links = (weakest[2] || []).filter(Boolean);
    const lead = T['rec_' + weakest[0]] || '';
    if (!lead && !links.length) return '';
    return `<div class="elt-card lg-card elt-recs">
        <div class="elt-block-t">${esc(T.recs_title)}</div>
        <p>${esc(lead)}</p>
        ${links.length ? '<div class="elt-row">' + links.map(l => `<a class="elt-btn ghost elt-btn-a" href="${esc(recLink(l.href))}">${esc(l.label)}</a>`).join('') + '</div>' : ''}
    </div>`;
}

// ── history / trend ───────────────────────────────────────────────────────

function historyListHtml(h) {
    return `<table class="elt-table elt-hist-table"><tbody>${h.slice().reverse().map(e => `
        <tr><td>${new Date(e.ts).toLocaleDateString(cfg.locale)}</td>
            <td>${esc(e.mode)}</td>
            <td><b>${esc(e.withheld ? (e.lo + '–' + e.hi) : e.level)}</b></td>
            <td>${esc(e.lo)}–${esc(e.hi)}</td></tr>`).join('')}</tbody></table>
    <button type="button" class="elt-btn ghost elt-btn-sm" id="eltHistClear">${esc(T.history_clear)}</button>
    <div class="elt-hist-note">${esc(T.history_note)}</div>`;
}

function historyBlock() {
    const h = history();
    if (h.length < 1) return '';
    return `<details class="elt-hist-toggle" ${h.length > 1 ? 'open' : ''}><summary>${esc(tr('history_summary', { n: h.length }))}</summary>
        ${h.length > 1 ? '<canvas id="eltTrend" width="800" height="180"></canvas>' : ''}
        <div id="eltHistList">${historyListHtml(h)}</div></details>`;
}

function bindHistoryClear() {
    const btn = $('eltHistClear');
    if (btn) btn.addEventListener('click', () => {
        try { localStorage.removeItem(LS_HISTORY); } catch (e) {}
        // «Очистить историю» — это «забудь меня на этом устройстве». Оставить
        // после неё отложенный результат значило бы выполнить обещание
        // наполовину: строки нет, а экран возвращается.
        clearPending();
        document.querySelectorAll('.elt-hist-toggle').forEach(el => el.remove());
    });
}

function drawTrend() {
    const cv = $('eltTrend');
    if (!cv) return;
    const h = history();
    if (h.length < 2) return;
    const ctx = cv.getContext('2d');
    const W = cv.width, H = cv.height, padL = 36, padB = 18, padT = 8;
    ctx.clearRect(0, 0, W, H);
    const thLo = -3, thHi = 2.8;
    const y = (th) => padT + (1 - (th - thLo) / (thHi - thLo)) * (H - padT - padB);
    const x = (i) => padL + i / Math.max(1, h.length - 1) * (W - padL - 12);
    ctx.font = '12px system-ui';
    ctx.fillStyle = 'rgba(160,170,190,.8)';
    ctx.strokeStyle = 'rgba(160,170,190,.25)';
    const anchors = { A1: -2.2, A2: -1.4, B1: -0.6, B2: 0.4, C1: 1.2, C2: 2.0 };
    for (const lv of LEVELS) {
        const yy = y(anchors[lv]);
        ctx.beginPath(); ctx.moveTo(padL, yy); ctx.lineTo(W - 8, yy); ctx.stroke();
        ctx.fillText(lv, 6, yy + 4);
    }
    ctx.strokeStyle = '#38bdf8'; ctx.lineWidth = 2; ctx.beginPath();
    h.forEach((e, i) => { const xx = x(i), yy = y(e.theta); i ? ctx.lineTo(xx, yy) : ctx.moveTo(xx, yy); });
    ctx.stroke();
    ctx.fillStyle = '#38bdf8';
    h.forEach((e, i) => { ctx.beginPath(); ctx.arc(x(i), y(e.theta), 3.5, 0, Math.PI * 2); ctx.fill(); });
}

// ── share card / pdf ──────────────────────────────────────────────────────

function shareCard(result) {
    const cv = document.createElement('canvas');
    cv.width = 1200; cv.height = 630;
    const ctx = cv.getContext('2d');
    // dark brand background
    const g = ctx.createLinearGradient(0, 0, 1200, 630);
    g.addColorStop(0, '#0b1220'); g.addColorStop(1, '#101b33');
    ctx.fillStyle = g; ctx.fillRect(0, 0, 1200, 630);
    ctx.fillStyle = 'rgba(56,189,248,.12)';
    ctx.beginPath(); ctx.arc(1050, 80, 220, 0, Math.PI * 2); ctx.fill();
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(170,185,210,.95)';
    ctx.font = '600 34px system-ui, sans-serif';
    ctx.fillText(T.share_card_eyebrow, 80, 110);
    // big level
    ctx.fillStyle = '#38bdf8';
    ctx.font = '800 220px system-ui, sans-serif';
    const levelTxt = result.levelWithheld ? (result.rangeLo + '–' + result.rangeHi) : result.level;
    ctx.fillText(levelTxt, 74, 330);
    ctx.fillStyle = '#e8eefc';
    ctx.font = '700 54px system-ui, sans-serif';
    ctx.fillText(result.levelWithheld ? T.withheld_title : (LEVEL_NAMES[result.level] || ''), 80, 408);
    // honesty line — the range goes on the share card too; that IS the brand
    ctx.fillStyle = 'rgba(170,185,210,.9)';
    ctx.font = '400 30px system-ui, sans-serif';
    ctx.fillText(tr('result_range', { lo: result.rangeLo, hi: result.rangeHi }) + ' · ' + tr('result_likely', { p: Math.round(result.levelP * 100) }), 80, 458);
    // mini skill bars
    const ps = result.perSkill;
    const bars = [[T.skill_vocab, ps.yesno || ps.vocab], [T.skill_grammar, ps.grammar], [T.skill_reading, ps.reading], [T.skill_listening, ps.listening]];
    let bx = 80, by = 510;
    ctx.font = '500 22px system-ui, sans-serif';
    for (const [label, sub] of bars) {
        ctx.fillStyle = 'rgba(170,185,210,.85)';
        ctx.fillText(label, bx, by - 10);
        ctx.fillStyle = 'rgba(255,255,255,.12)';
        ctx.fillRect(bx, by, 220, 12);
        if (sub) {
            ctx.fillStyle = '#38bdf8';
            ctx.fillRect(bx, by, 220 * thetaToPct(sub.theta) / 100, 12);
        }
        bx += 270;
    }
    ctx.fillStyle = 'rgba(170,185,210,.7)';
    ctx.font = '400 24px system-ui, sans-serif';
    ctx.fillText(T.share_card_footer, 80, 590);

    cv.toBlob((blob) => {
        if (!blob) return;
        const file = new File([blob], 'english-level.png', { type: 'image/png' });
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
            navigator.share({ files: [file], title: T.share_card_eyebrow }).catch(() => {});
        } else if (window.__downloadFile) {
            window.__downloadFile(blob, 'english-level.png');
        }
    }, 'image/png');
}

function exportPdf(result) {
    const el = $('eltResults');
    if (!el) return;
    if (typeof window.__generatePDF !== 'function') {
        window.__toast && window.__toast(T.load_error, 'error');
        return;
    }
    window.__generatePDF({
        element: el,
        filename: 'english-level-report.pdf',
        title: T.pdf_title,
    });
}

// ── keyboard navigation ────────────────────────────────────────────────────
// One document-level handler reads the live DOM and maps keys to the visible
// unit. Layout-independent: digits use e.code (Digit/Numpad), so a Cyrillic or
// AZERTY layout still answers with the physical number row. Ignored while the
// user types in any field (e.g. the sidebar search).
function installKeyboard() {
    document.addEventListener('keydown', (e) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const ae = document.activeElement;
        if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (ae && (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) || ae.isContentEditable))) return;
        if (!root || !root.offsetParent) return;   // panel not visible

        const digit = /^(Digit|Numpad)([1-9])$/.exec(e.code);
        const click = (id) => { const el = $(id); if (el && !el.disabled) { el.click(); return true; } return false; };

        // voice chooser (sound check and listening items): V cycles the sets
        if (e.code === 'KeyV' && $('eltVoice')) {
            e.preventDefault();
            const next = document.querySelector('#eltVoice button[aria-pressed="false"]:not([disabled])');
            if (next) next.click();
            return;
        }
        // volume check
        if ($('eltVolPlay')) {
            if (e.key === ' ' || e.code === 'KeyP') { e.preventDefault(); click('eltVolPlay'); return; }
            if (e.key === 'Enter') { if (click('eltVolOk')) e.preventDefault(); return; }
            return;
        }
        // section / passage continue
        if ($('eltSecGo')) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); click('eltSecGo'); } return; }
        if ($('eltPassGo')) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); click('eltPassGo'); } return; }
        // yes / no vocab
        if ($('eltYes')) {
            if (e.code === 'KeyY' || e.key === 'ArrowRight' || (digit && digit[2] === '2')) { e.preventDefault(); click('eltYes'); return; }
            if (e.code === 'KeyN' || e.key === 'ArrowLeft' || (digit && digit[2] === '1')) { e.preventDefault(); click('eltNo'); return; }
            return;
        }
        // listening: play with Space/P until options unlock
        const playBtn = $('eltPlay');
        if (playBtn) {
            const opts = document.querySelectorAll('.elt-opt:not([disabled])');
            if (!opts.length && (e.key === ' ' || e.code === 'KeyP')) { e.preventDefault(); playBtn.click(); return; }
        }
        // MCQ options 1–N
        if (digit) {
            const opts = document.querySelectorAll('.elt-opt');
            const idx = +digit[2] - 1;
            if (opts[idx] && !opts[idx].disabled) { e.preventDefault(); opts[idx].click(); }
        }
    });
}

// ── abandonment telemetry ──────────────────────────────────────────────────
// Fire once when a started-but-unfinished test leaves the page (close, nav
// away, tab hidden). This is the single most valuable funnel metric — it shows
// exactly which stage users drop at. pagehide + visibilitychange covers
// desktop close, mobile app-switch, and bfcache.
function installAbandon() {
    const fire = () => {
        if (!session || testCompleted || abandonFired) return;
        abandonFired = true;
        let n = null;
        try { n = (session.serialize().responses || []).length; } catch (e) {}
        try { window.__aeFeature && window.__aeFeature('abandon', { mode, stage: currentStage, n }); } catch (e) {}
        try { window.__aeFlush && window.__aeFlush(); } catch (e) {}
    };
    window.addEventListener('pagehide', fire);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') fire(); });
}

// ── boot ──────────────────────────────────────────────────────────────────

/**
 * Вернуть человека к его результату, если он уходил со страницы.
 *
 * Зовётся ВМЕСТО renderModeSelect(): показать выбор режима тому, кто двадцать
 * минут назад закончил тест и вышел на регистрацию, — и есть то самое «всё
 * сбросилось».
 *
 * ⚠️ Ловушки defer/inline, которая стоила /iq-test восемнадцати дней, здесь
 * НЕТ и полагаться на это можно: level-test-offer.js подключён классическим
 * тегом ВЫШЕ (english-level-test.blade.php), а этот файл — type="module", то
 * есть отложенный. Значит window.EltOffer к моменту init() уже существует, и
 * renderResults() смонтирует панель документов с кнопкой оплаты.
 *
 * @returns {boolean} восстановили ли экран
 */
function restorePending() {
    const o = pendingResult();
    if (!o) return false;
    try {
        mode = o.mode || o.result.__mode || 'full';
        // Прогон завершён в прошлой загрузке: без этого installAbandon()
        // посчитал бы восстановленный экран брошенным тестом и исказил бы
        // главную воронку тула.
        testCompleted = true;
        renderResults(o.result);
    } catch (e) {
        // Заначка не той формы (старая версия банка, обрезанный JSON) — она не
        // повод показать сломанный экран. Убираем и уходим на обычный старт.
        clearPending();
        try { window.__aeToolError && window.__aeToolError({ error_type: 'pending_restore_failed', msg: String(e && e.message || e).slice(0, 120) }); } catch (e2) {}
        return false;
    }
    fe('result_restored', { mode });
    return true;
}

(async function init() {
    let onStart = false;   // init ends on the mode select with nothing to continue
    try {
        installKeyboard();
        installAbandon();
        bank = await loadBank();
        const v = Cat.validateBank(bank);
        if (!v.ok) throw new Error('bank invalid: ' + v.errors[0]);
        calibSets = bank.calib;
        voiceSetsUsable = bankHonoursVoiceSets(bank.listening);
        if (!voiceSetsUsable) {
            // Code and data are different generations. Never silent: this is
            // exactly the state that shipped a dead chooser for two days.
            fe('voice_sets_missing', { url: bankUrl('listening').slice(-64) });
        }
        root.removeAttribute('data-boot');
        // No #eltStartScreen (stale cached page): renderModeSelect() shows the
        // load error, and a tap has nothing to start.
        if (!restorePending()) onStart = !renderModeSelect() && !!startNode;
    } catch (e) {
        root.removeAttribute('data-boot');
        root.innerHTML = '<div class="elt-card lg-card elt-center">' + esc(T.load_error) + '</div>';
        onStart = false;
        try { window.__aeToolError && window.__aeToolError({ error_type: 'bank_load_failed', msg: String(e && e.message || e).slice(0, 120) }); } catch (e2) {}
    }
    // Outside the try: a replayed tap is the person's own start, and a fault in
    // it is not a bank that failed to load.
    claimEarly(onStart);
})();
